import { createTheme, PaletteColor, Theme, ThemeOptions } from "@mui/material/styles";
import shadows, { Shadows } from "@mui/material/styles/shadows";

// https://mui.com/x/react-date-pickers/getting-started/#typescript
import type {} from "@mui/x-date-pickers/themeAugmentation";

/**
 * This allows us to type the theme provided by `makeStyles(theme => ({...}))`
 *
 * https://mui.com/material-ui/migration/v5-style-changes/#%E2%9C%85-add-module-augmentation-for-defaulttheme-typescript
 */
declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

export const FontProxima = ["proxima-soft", "sans-serif"].join(",");
export const FontRoboto = ["Roboto", "Arial"].join(",");

export let v2theme = createTheme({
  spacing: (factor: number) => `${0.25 * factor}rem`,
  palette: {
    neutral: {
      main: "#D0D0D0",
      dark: "#747474",
      light: "#FFFFFF",
      contrastText: "#747474",
    },
    flush: {
      main: "#0092E0",
      dark: "#0D89D2",
      light: "#E2F7FF",
      contrastText: "#FFF",
    },
    primary: {
      main: "#0092E0",
      dark: "#0D89D2",
      light: "#E2F7FF",
    },
    secondary: {
      main: "#E74873",
      dark: "#cf4469",
      light: "#ffcfdc",
    },
    info: {
      main: "#80449F",
      dark: "#4E2960",
      light: "#A67CBC",
    },
    divider: "#DEDEDE",
    success: {
      main: "#5BA621",
      dark: "#569e20",
      light: "#dfffb8",
    },
    warning: {
      main: "#F76205",
      dark: "#e66007",
      light: "#ffbe96",
    },
    error: {
      main: "#B72A2A",
      dark: "#9c2828",
      light: "#ffa3a3",
    },
    text: {
      primary: "#191919",
      secondary: "#5d5d5d",
    },
  },
  typography: {
    fontFamily: FontProxima,
    body1: {
      fontFamily: FontRoboto,
    },
    body2: {
      fontFamily: FontRoboto,
    },
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    button: {
      textTransform: "none",
      fontSize: "1rem",
      fontWeight: "bold",
      lineHeight: "1",
    },
  },
});

// This allows us to reuse parts of the theme
// https://mui.com/material-ui/customization/theming/#api:~:text=Theme%20composition%3A%20using%20theme%20options%20to%20define%20other%20options
// font size should be take in em or %age to resolve a11y issues
// 16px = 1em
// every 2px = 0.125em
v2theme = createTheme(v2theme, {
  // disable shadows universally
  shadows: shadows.map(() => "none") as Shadows,
  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontFamily: FontRoboto,
        },
      },
    },
    MuiDayPicker: {
      styleOverrides: {
        weekDayLabel: {
          fontFamily: FontRoboto,
        },
      },
    },
    PrivatePickersYear: {
      styleOverrides: {
        button: {
          fontFamily: FontRoboto,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: "2.5em",
          letterSpacing: 0,
          fontWeight: v2theme.typography.fontWeightBold,
        },
        h2: {
          fontSize: "2.25em",
          letterSpacing: 0,
          fontWeight: v2theme.typography.fontWeightBold,
        },
        h3: {
          fontSize: "1.75em",
          letterSpacing: "0.28px",
          fontWeight: v2theme.typography.fontWeightBold,
        },
        h4: {
          fontSize: "1.5em",
          letterSpacing: 0,
          fontWeight: v2theme.typography.fontWeightBold,
        },
        h5: {
          fontSize: "1.25em",
          letterSpacing: 1.6,
          fontWeight: v2theme.typography.fontWeightRegular,
          color: v2theme.palette.text.secondary,
        },
        h6: {
          fontSize: "1em",
          letterSpacing: 0,
          fontWeight: v2theme.typography.fontWeightRegular,
          color: v2theme.palette.text.secondary,
        },
        body1: {
          fontFamily: FontRoboto,
          fontSize: "1em",
          fontWeight: v2theme.typography.fontWeightRegular,
        },
        body2: {
          fontFamily: FontRoboto,
          fontSize: "1em",
          fontWeight: v2theme.typography.fontWeightRegular,
          color: v2theme.palette.text.secondary,
        },
        subtitle1: {
          fontFamily: FontRoboto,
          fontSize: "1em",
          fontWeight: v2theme.typography.fontWeightRegular,
        },
        subtitle2: {
          fontFamily: FontRoboto,
          fontSize: "1em",
          fontWeight: v2theme.typography.fontWeightBold,
        },
      },
    },
    MuiButtonBase: {
      // disable ripple universally
      // defaultProps: {
      //   disableRipple: true,
      // },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `2px solid ${v2theme.palette.divider}`,
          borderRadius: 15,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: v2theme.palette.text.primary,
          fontWeight: 600,
          "&:hover": {
            boxSizing: "border-box",
            borderBottom: `4px solid ${v2theme.palette.primary.main}`,
            borderTop: `4px solid transparent`,
            backgroundColor: "#FAFAFA",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: "8px",
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          color: "#FFFFFF",
          transitionProperty: "transform, color, border-color, background-color",
          transitionDuration: "150ms",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        },
        contained: {
          "&:hover": {
            // backgroundColor: v2theme.palette.primary.dark,
          },
        },
        outlined: {
          backgroundColor: "white",
          border: `2px solid ${v2theme.palette.divider}`,
          color: v2theme.palette.primary.main,
          "&:hover": {
            border: `2px solid ${v2theme.palette.primary.main}`,
            backgroundColor: v2theme.palette.primary.light,
            color: v2theme.palette.primary.main,
          },
        },
        sizeSmall: {
          height: 37,
          padding: "12px 16px",
        },
        sizeMedium: {
          height: 46,
          padding: "16px 32px",
        },
        sizeLarge: {
          height: 62,
          padding: "21px 32px",
          fontSize: 19,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: v2theme.palette.text.secondary,
        },
        sizeSmall: {
          width: 32,
          height: 32,
        },
        sizeMedium: {
          width: 48,
          height: 48,
        },
        sizeLarge: {
          width: 64,
          height: 64,
        },

        // Outlined and contained variants don't exist in MUI
        // See the custom IconButton component
      },
    },
    MuiCalendarPicker: {
      styleOverrides: {
        root: {
          border: `2px solid ${v2theme.palette.divider}`,
          borderRadius: 8,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: FontRoboto,
          color: v2theme.palette.text.secondary,
          textDecorationColor: v2theme.palette.text.secondary,
          cursor: "pointer",
          transitionProperty: "color, textDecorationColor",
          transitionDuration: "150ms",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            color: v2theme.palette.text.primary,
            textDecorationColor: v2theme.palette.text.primary,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: 2,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {},
        paper: {
          borderRadius: "8px",
        },
        list: {
          padding: 12,
          borderRadius: 8,
          border: `2px solid ${v2theme.palette.divider}`,
          backgroundColor: "none",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          textDecoration: "none",
          fontFamily: FontRoboto,
          fontWeight: v2theme.typography.fontWeightMedium,
          color: v2theme.palette.text.secondary,
          "&:hover": {
            color: v2theme.palette.text.primary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: "#323233",
        },
        tooltip: {
          fontFamily: FontRoboto,
          color: "#ffffff",
          backgroundColor: "#323233",
          fontSize: "1em",
        },
        popper: {
          zIndex: 9999999 + "!important", // adding highest z-index because tooltips were not visibile on modal since it was moved to modal context
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          borderRadius: 4,
          boxShadow: "0px 3px 3px #00000061",
          zIndex: 9999999 + "!important", // adding highest z-index because auto complete were not visibile on modal since it was moved to modal context
        },
        listbox: {
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D8D8D8",
            borderRadius: 4,
          },
        },
        option: {
          marginLeft: 8,
          marginRight: 8,
          borderRadius: 15,
          "&:hover": {
            backgroundColor: "#0000000A",
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: 5000,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent",
            "& svg": {
              color: v2theme.palette.primary.main,
              backgroundImage: `radial-gradient(${v2theme.palette.primary.light},${v2theme.palette.primary.light} 40%,transparent 50%)`,
            },
          },
        },
      },
    },
  },
} as ThemeOptions);

declare module "@mui/material/styles" {
  interface Palette {
    neutral: PaletteColor;
    flush: PaletteColor;
  }
  interface PaletteOptions {
    neutral: PaletteColor;
    flush: PaletteColor;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true;
    flush: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    neutral: true;
    flush: true;
  }
}
